// Spec point 7: three marketing consent checkboxes - e-mail, SMS, messengers - on checkout.
// The fields ride along in the existing POST v1/checkout/store body; there is no separate
// endpoint and nothing about consent comes back in the response.
//
// The wordings and the version now come from GET v1/consent-texts, kind `checkout_marketing`
// (see @/constants/consent-texts). They are read from one object and sent back from that same
// object, so the version can never name a revision other than the one on screen. Nothing here
// falls back to the translation table: a version from the backend shown against a wording from
// the translation sheet would be evidence of agreement to a text nobody can reconstruct.

// The order of the checkboxes is ours, not the JSON's - object key order is not a contract and
// what a visitor reads first is a design decision. `apiKey` is the name the published text and
// the consent journal use; `field` is the name the checkout body has always used. They differ in
// number for the third channel, and this is the one place that knows it.
export const MARKETING_CONSENT_CHANNELS = [
  { key: 'email', apiKey: 'email', field: 'marketing_consent_email' },
  { key: 'sms', apiKey: 'sms', field: 'marketing_consent_sms' },
  { key: 'messengers', apiKey: 'messenger', field: 'marketing_consent_messengers' },
]

export function emptyMarketingConsent() {
  return MARKETING_CONSENT_CHANNELS.reduce((consent, channel) => {
    consent[channel.key] = false
    return consent
  }, {})
}

// The channels the published revision actually carries a wording for, in our order. Empty until
// the text is published, and while it is empty the checkout sends no consent fields at all -
// the state the backend treats as "feature not deployed yet", which is what keeps their release
// and ours independent of each other.
export function marketingConsentChannelsFrom(text) {
  const channels = (text && text.channels) || null
  if (!channels) return []

  return MARKETING_CONSENT_CHANNELS.filter((channel) => channels[channel.apiKey]).map(
    (channel) => ({ ...channel, text: channels[channel.apiKey] }),
  )
}

// The version is mandatory as soon as any one of the flags is present - even when all of them
// are false, since an unticked box is a decision too and gets its own journal row. A flag is
// sent only for a channel that was actually on screen: sending one for a channel whose wording
// the revision does not carry would journal a refusal to a question nobody was asked. Strings
// "true"/"false" fail validation, so the values are real booleans.
export function buildMarketingConsentPayload(consent, channels, version) {
  return channels.reduce(
    (payload, channel) => {
      payload[channel.field] = !!(consent && consent[channel.key])
      return payload
    },
    { marketing_consent_version: version },
  )
}

// `answers` is GET v1/user/marketing-consents: per journal key (`apiKey`), `true` agreed, `false`
// refused, `null` never asked. A signed-in user who has answered every channel on screen is not
// asked again at checkout, and no consent fields are sent - without them the journal writes
// nothing and the earlier decision stands. One `null` is enough to ask again, all four options.
export function marketingConsentAnswered(channels, answers) {
  if (!answers || !channels.length) return false
  return channels.every((channel) => typeof answers[channel.apiKey] === 'boolean')
}

// Checkout asks for an explicit answer: at least one channel, or "none of it". Only when the
// revision carries "none of it" - without it the only way through would be to agree, and a
// consent nobody could refuse is no consent. The backend accepts the blank form; this is ours.
export function marketingConsentMissing(consent, channels, optOutShown) {
  if (!optOutShown || !channels.length) return false
  if (consent && consent.optOut) return false
  return !channels.some((channel) => consent && consent[channel.key])
}

// The profile screen opens on the saved decision: a channel agreed to is ticked, and when every
// channel on screen was refused, "none of it" is ticked instead. A channel never asked about
// starts unticked, like at checkout.
export function marketingConsentFromAnswers(channels, answers) {
  const consent = emptyMarketingConsent()
  if (!answers) return consent

  channels.forEach((channel) => {
    consent[channel.key] = answers[channel.apiKey] === true
  })
  consent.optOut = channels.length > 0 && channels.every((channel) => answers[channel.apiKey] === false)

  return consent
}
